import { useState } from 'react';
import type { AdminCreatorAnswerCount, AdminCreatorOnboardingSummary } from '@boardflight/shared';
import { BUILD_KIND_LABELS, CREATOR_ROLE_LABELS, HEARD_FROM_LABELS } from '@boardflight/shared';
import { api } from '../api.js';
import { Async, Panel, Time, useAsync } from '../ui.js';

/**
 * What new creators said in the dashboard's onboarding modal (`docs/creator-onboarding.md`).
 *
 * Read-only, like Onboarding beside it — that tab is the Board client's funnel, this one is the
 * dashboard's. Counts are accounts whose answers were *given* in the range; "still owed" is the
 * accounts that signed up in it and have not opened a team page on a desktop since.
 */
export function Creators() {
  const [days, setDays] = useState(30);
  const state = useAsync(
    () => api<AdminCreatorOnboardingSummary>(`/v1/admin/creator-onboarding?days=${days}`),
    [days],
  );

  return (
    <>
      <Panel
        title="Creator onboarding"
        actions={
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        }
      >
        <Async state={state}>
          {(data) => (
            <>
              <div className="stats">
                <div className="stat"><span className="stat-label">Answered</span><span className="stat-value">{data.answered}</span></div>
                <div className="stat"><span className="stat-label">Skipped</span><span className="stat-value">{data.skipped}</span></div>
                <div className="stat" title="Signed up in the range and not asked yet: no team page opened on a desktop, or Not now pressed.">
                  <span className="stat-label">Not asked yet</span><span className="stat-value">{data.owed}</span>
                </div>
              </div>
              <p className="note">Accounts from before the questions existed were never asked and are in none of these.</p>
            </>
          )}
        </Async>
      </Panel>

      <Panel title="How they heard about BoardFlight">
        <Async state={state}>{(data) => <Counts rows={data.heardFrom} of={data.answered} />}</Async>
      </Panel>

      <Panel title="Who they are">
        <Async state={state}>{(data) => <Counts rows={data.roles} of={data.answered} />}</Async>
      </Panel>

      <Panel title="What they want to build">
        <Async state={state}>
          {(data) => (
            <>
              <p className="note">Several can apply, so the shares add up to more than 100%.</p>
              <Counts rows={data.building} of={data.answered} />
            </>
          )}
        </Async>
      </Panel>

      <Panel title="In their own words">
        <Async state={state} empty="Nobody wrote anything in this range.">
          {(data) =>
            data.notes.length === 0 ? null : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Handle</th>
                      <th>Heard from</th>
                      <th>Who</th>
                      <th>Building</th>
                      <th>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.notes.map((note) => (
                      <tr key={note.userId}>
                        <td className="mono"><a href={`#accounts/${note.userId}`}>@{note.username}</a></td>
                        <td>
                          {label(HEARD_FROM_LABELS, note.heardFrom)}
                          {note.heardFromOther ? <div className="note">“{note.heardFromOther}”</div> : null}
                        </td>
                        <td>
                          {label(CREATOR_ROLE_LABELS, note.role)}
                          {note.roleOther ? <div className="note">“{note.roleOther}”</div> : null}
                        </td>
                        <td>
                          {note.building.map((kind) => label(BUILD_KIND_LABELS, kind)).join(', ') || '—'}
                          {note.buildingNote ? <div className="note">“{note.buildingNote}”</div> : null}
                        </td>
                        <td>
                          <Time at={note.createdAt} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </Async>
      </Panel>
    </>
  );
}

function label(labels: Record<string, string>, key: string | null): string {
  if (key === null) return '—';
  return labels[key] ?? key;
}

function Counts({ rows, of }: { rows: AdminCreatorAnswerCount[]; of: number }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Answer</th>
            <th className="num">Accounts</th>
            <th className="num">Of answered</th>
            <th style={{ width: '34%' }} />
            <th className="num" title="Of these accounts, the ones that have since uploaded a build">Shipped a build</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>
                {row.label}
                {row.known ? null : (
                  <span className="pill" style={{ marginLeft: 6 }}>retired</span>
                )}
              </td>
              <td className="num">{row.accounts}</td>
              <td className="num muted">{of === 0 ? '—' : `${Math.round((row.accounts / of) * 100)}%`}</td>
              <td>
                <div
                  style={{
                    height: 8,
                    marginTop: 7,
                    borderRadius: 4,
                    background: 'var(--line)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: of === 0 ? 0 : `${Math.min(100, (row.accounts / of) * 100)}%`,
                      background: 'var(--accent)',
                    }}
                  />
                </div>
              </td>
              <td className="num">
                {row.shipped}
                {row.accounts ? <span className="muted small"> · {Math.round((row.shipped / row.accounts) * 100)}%</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
